/**
 * ENG-8876: per-agent PID pressure, so the ENG-8825 class of failure is VISIBLE
 * before it bites instead of after.
 *
 * ENG-8825 added `--init` to the agent container so adopted orphans get reaped.
 * That stopped the leak. It added no way to SEE a leak, so the next thing that
 * eats PIDs — a different leak, a runaway fanout, a genuinely busy agent — puts
 * us back in the same place with the same blindness.
 *
 * ## Why this one deserves a gauge more than most resource limits
 *
 * The disguise. Past `--pids-limit` every spawn returns EAGAIN, and that surfaces
 * inside whatever unrelated command ran next: `vitest` reports "no tests" (reads
 * as a broken suite), `tsc` dies at exit 137, `esbuild` fails mid-run. An agent
 * hitting this concludes ITS OWN CODE is wrong. It does not report a resource
 * problem, because from the inside it does not look like one.
 *
 * ## Exit 137 is ambiguous, and this module must not claim otherwise
 *
 * On ENG-8825 a `tsc` exit 137 was misdiagnosed as an OOM three times before
 * anyone checked the PID count. The tempting lesson — "137 means PIDs" — is
 * wrong, and installing it would just invert the same error.
 *
 * Measured while writing this module (2026-08-14): a `tsc --noEmit` on
 * `packages/api` died at exit 137 on a live agent whose cgroup read **115/512
 * tasks with zero defunct**. `memory.events` showed `oom_kill 2` against a 2 GiB
 * cap. That 137 was a genuine OOM, in the same container, on the same day, while
 * PID pressure was at 22%.
 *
 * 137 is SIGKILL. It says the kernel killed the process, not why. **Both**
 * `/sys/fs/cgroup/pids.current` and `memory.events` have to be read before
 * attributing it — which is the whole argument for having this gauge at all:
 * not so 137 can be blamed on PIDs, but so the question can be answered from
 * data instead of from whichever cause was most recently memorable.
 *
 * A number on a dashboard is worth more here than for a limit that fails
 * legibly.
 *
 * ## The constraint that shapes everything below: the manager runs on the HOST
 *
 * `collectDiagnostics` runs in the manager process, on the host. The agent runs
 * INSIDE a container — the same module builds its `docker run` and injects
 * `-e AGT_IN_CONTAINER=true` precisely so code inside can tell the difference.
 *
 * So this module must NOT read `/sys/fs/cgroup/pids.current`. From the host that
 * path is the HOST's own cgroup. It would return a plausible number that is
 * silently about the wrong thing, which is the worst kind of wrong: a gauge that
 * looks like it works. The agent's cgroup has to be addressed explicitly, by
 * container id, from the host side.
 *
 * ## Why not `docker exec`
 *
 * `docker exec agt-<code> cat /sys/fs/cgroup/pids.current` would be a one-liner.
 * It is rejected on two grounds, the second of which is the real one:
 *
 *  1. 50–150ms per agent per cycle, on a manager that already does network I/O
 *     and per-agent tmux/docker work before re-arming.
 *  2. **Spawning a subprocess per agent per cycle TO MEASURE PID PRESSURE
 *     consumes PIDs.** If those children were ever imperfectly reaped, the
 *     monitor becomes the disease it was written to detect — and it would do so
 *     invisibly, because the thing that would have shown us is the thing that is
 *     broken.
 *
 * Instead: resolve the container id ONCE, cache it, and let every subsequent
 * sample be a plain `readFileSync`. Nothing is spawned on the sampling path.
 *
 * ## `pids.current` counts TASKS, not processes
 *
 * Measured inside a live agent container (koda, 2026-08-14 09:58Z):
 *
 *     /sys/fs/cgroup/pids.current  ->  76
 *     ps -e | wc -l                ->  12
 *
 * A 6x gap, because the cgroup pids controller counts threads and node runs a
 * lot of them. A sampler that counted processes would read 2% of a 512 ceiling
 * while the cgroup sat at 15%, and would therefore alert far too late — which is
 * precisely the failure this module exists to prevent. The signal is
 * `pids.current` and nothing else.
 *
 * ## Absent means NO SIGNAL, never zero
 *
 * A bare-tmux (non-isolated) agent has no per-agent cgroup, so there is nothing
 * to measure and this returns `null`. Consumers must SKIP a null, never coerce
 * it to 0 — a 0 would render every non-containerised agent permanently and
 * invisibly green. Same contract as `claudeMd` in {@link SessionDiagnostics}.
 */

import { execFileSync } from 'node:child_process';
import { hardBoundedCapture } from './bounded-exec.js';
import { existsSync, readFileSync } from 'node:fs';

import { isolationMode } from './persistent-session.js';

/**
 * How often the sampler ticks.
 *
 * Deliberately slower than the pane-occupancy sampler's 10s: PID pressure is a
 * LEVEL, not a duration, so no accrual is lost by sampling coarsely, and the
 * observed leak rate (~100 PIDs/hour in the ENG-8825 incident) moves this number
 * by ~1 per 36 seconds. A 30s cadence resolves that with room to spare while
 * staying well inside the heartbeat's own ~90s spacing, so every heartbeat
 * carries a reading no more than one interval stale.
 */
export const SAMPLE_INTERVAL_MS = 30_000;

/**
 * Fraction of the ceiling at which the reading is flagged.
 *
 * 0.8 of the default 512 is 409. In the ENG-8825 incident the agent was found at
 * 479/512 by hand; at the measured ~100/hour that threshold would have flagged
 * it roughly 40 minutes earlier, which is the difference between "an operator
 * looks at it" and "every subsequent tool call lies about why it failed".
 *
 * Env-overridable per host for the same reason the ceiling itself is
 * (`AGT_ISOLATION_PIDS`): a host running unusually fan-out-heavy agents may sit
 * legitimately high, and the alternative to a knob is someone hardcoding a
 * second threshold somewhere else.
 */
export function warnFraction(): number {
  const raw = Number(process.env.AGT_PID_PRESSURE_WARN_FRACTION);
  if (!Number.isFinite(raw) || raw <= 0 || raw > 1) return 0.8;
  return raw;
}

/**
 * The ceiling the host actually configured.
 *
 * Read from `AGT_ISOLATION_PIDS` — the SAME env var `buildDockerRunCommand`
 * passes to `--pids-limit` — rather than a second hardcoded 512. Two constants
 * that are supposed to agree eventually will not, and the failure would be
 * silent: an alarm calibrated to a ceiling the container does not have.
 */
export function configuredCeiling(): number {
  const raw = Number(process.env.AGT_ISOLATION_PIDS);
  return Number.isFinite(raw) && raw > 0 ? raw : 512;
}

export interface PidPressureSample {
  /**
   * `pids.current` — TASKS (threads), not processes. See the module docstring;
   * this being threads is the whole reason the naive version of this is wrong.
   */
  current: number;
  /**
   * `pids.max` as the KERNEL enforces it. `null` when the cgroup reports `max`
   * (no limit), which is a real state and not an error.
   */
  max: number | null;
  /**
   * The ceiling the HOST configured (`AGT_ISOLATION_PIDS`). Consumers compare
   * against this rather than a second hardcoded constant, so the two sides
   * cannot disagree.
   *
   * Carried ALONGSIDE `max` on purpose: if these two ever differ, the container
   * is not running with the limit this host thinks it set, and that config drift
   * is worth seeing rather than averaging away.
   */
  ceiling: number;
  /** Process-table `Z` (defunct) count inside the agent's cgroup. */
  defunct: number;
  /** The absolute value `current` is flagged against. */
  warnAt: number;
  overThreshold: boolean;
  /**
   * ENG-10370: the memory counters from the SAME cgroup dir, or null when
   * `memory.events` could not be read or understood.
   *
   * Null is a real state and is not the same as zero - see
   * {@link parseMemoryEvents}. A consumer that treats null as "no kills" would
   * rebuild the exact false-healthy reading this module exists to prevent.
   */
  memory: MemoryPressureSample | null;
}

/**
 * ENG-10370: the memory half of the same cgroup, read on the same pass.
 *
 * `host_events` has carried an `oom_kill` kind since 8 May and the host events
 * panel has had a label ready to render it for just as long. Nothing has ever
 * written one. Meanwhile the condition is frequent: measured on THIS container
 * while writing this (2026-09-14), `memory.events` read `oom_kill 2` and
 * `max 15003` against a 2 GiB ceiling, with `memory.peak` (2147487744) ABOVE
 * `memory.max` (2147483648). An operator opening that panel sees a surface that
 * can display "OOM kill" and never will, which reads as coverage.
 *
 * Both counters are carried, and which is which matters:
 *
 *  - `oomKill` is LAGGING. It counts kills already suffered. By the time it
 *    moves, an agent has already lost a process — and, per this module's own
 *    docstring, has probably already misdiagnosed the resulting exit 137 as its
 *    own bug.
 *  - `atCeiling` is LEADING. `memory.events` `max` counts the times the cgroup
 *    hit its ceiling and had to reclaim synchronously. A container can sit on a
 *    huge `max` with `oom_kill 0` — living against the wall, healthy-looking,
 *    one allocation away.
 *
 * WHAT IS ACTUALLY WIRED, because the paragraph above describes a capability and
 * a reader would otherwise assume it is in use (Obi Wan 18217b7e569c, and the
 * finding was correct). The detector this ticket ships fires SOLELY on
 * `oomKill > prior` — the lagging counter. `atCeiling` is sampled and carried
 * into the `oom_kill` event's `details` as `at_ceiling`, which means it can only
 * ever be read AFTER a kill has already happened. Nothing else consumes it: no
 * surface renders it, and the sibling metric batches carry only `current` and
 * `defunct`.
 *
 * So the leading indicator is recorded and is not yet leading anything. That is
 * a deliberate scope line rather than an oversight — a threshold detector on
 * `atCeiling` needs a rate, a baseline and an alarm policy, none of which this
 * ticket establishes — but saying "atCeiling is LEADING" without saying that
 * would claim coverage this change does not provide, which is the exact defect
 * class ENG-10370 exists to remove. Filed as ENG-10502, which carries the three
 * things a real detector needs and this ticket does not establish: a rate rather
 * than an absolute, a threshold justified by a measured fleet distribution, and
 * an alarm policy that states its expected false-positive rate.
 *
 * Named `atCeiling` rather than `max` on purpose: `PidPressureSample.max` in
 * this same module means "the pids ceiling", and two fields called `max` meaning
 * opposite kinds of thing (a limit vs a count of times a limit was hit) in one
 * object graph is how a consumer reads the wrong one.
 */
export interface MemoryPressureSample {
  /** `memory.events` `oom_kill` — kills suffered. MONOTONIC for the container's life. */
  oomKill: number;
  /** `memory.events` `max` — times the cgroup hit its ceiling and reclaimed. MONOTONIC. */
  atCeiling: number;
}

/**
 * Parse `memory.events`. Pure, so the shape can be tested without a cgroup.
 *
 * The file is `key value` lines. Kernels differ in WHICH keys they emit -
 * `oom_group_kill` is present on this host and absent on older ones - so this
 * reads the keys it wants by name and ignores everything else rather than
 * matching on position or asserting a set.
 *
 * Returns null rather than zeros when a key is missing or unparseable. That
 * distinction is the whole point of this ticket: a zero is a reading, and
 * reporting one for a file we could not understand would recreate the silent
 * false-healthy failure one layer down. The empty-string trap the pids read
 * documents applies identically - `Number('')` is 0 - so each value is checked
 * for blankness before coercion.
 */
export function parseMemoryEvents(text: string | null): MemoryPressureSample | null {
  if (text == null) return null;
  const wanted = new Map<string, number>();
  for (const line of text.split('\n')) {
    const [key, rawValue] = line.trim().split(/\s+/);
    if (key !== 'oom_kill' && key !== 'max') continue;
    // ONE guard, deliberately, and two earlier companions are gone because
    // MUTATION TESTING PROVED THEM EQUIVALENT - the same finding this module
    // already records for `pids.max`, arrived at the same way.
    //
    // What I wrote first was `rawValue == null || rawValue === ''`. Neither
    // branch is reachable as a DISTINCT rejection:
    //   - `=== ''` cannot fire at all. `line.trim()` before the split means a
    //     non-empty line never yields an empty second element, and a blank
    //     line's key is '' which neither key test above wants.
    //   - `== null` fires for a key with no value (`oom_kill\n`), but
    //     `Number(undefined)` is NaN, which the isFinite check below already
    //     rejects.
    // Deleting each in turn left the suite green, which is the definition.
    //
    // The `Number('') === 0` trap they were guarding against is real - it is
    // why the pids read carries its own long comment - it simply cannot arrive
    // through THIS parser's split. Leaving them in would read as though they
    // catch a case isFinite does not, and the next person would have to work
    // out that they don't.
    //
    // The coercions this now leans on, all landing on null: `Number(undefined)`
    // is NaN (key present, value absent) and `Number('abc')` is NaN (garbage).
    const value = Number(rawValue);
    if (!Number.isFinite(value) || value < 0) return null;
    wanted.set(key, value);
  }
  const oomKill = wanted.get('oom_kill');
  const atCeiling = wanted.get('max');
  if (oomKill === undefined || atCeiling === undefined) return null;
  return { oomKill, atCeiling };
}

/** Injectable I/O seam, so tests never touch a filesystem or spawn docker. */
export interface PidPressureIo {
  /** Absolute host-side cgroup directory for the agent's container, or null. */
  resolveCgroupDir?: (codeName: string) => string | null;
  /** Read a file as text; null when absent or unreadable. */
  readText?: (path: string) => string | null;
  /** Override the isolation check (tests). */
  isolatedFor?: (codeName: string) => boolean;
}

function readTextReal(path: string): string | null {
  try {
    return readFileSync(path, 'utf-8');
  } catch {
    return null;
  }
}

/**
 * codeName -> resolved host-side cgroup dir. Cached because resolving it is the
 * only step that costs a subprocess; see the module docstring on why that must
 * not happen per-sample.
 */
const cgroupDirCache = new Map<string, string>();

/**
 * codeName -> earliest time we may try `docker inspect` again after a FAILED
 * resolution.
 *
 * Without this, the module's headline guarantee is only true in the happy path.
 * A cache miss costs one subprocess, so an isolated agent whose container is
 * NOT running (stopped, mid-restart, crash-looping — exactly when things are
 * already going wrong) misses on every single tick and spawns a `docker
 * inspect` every {@link SAMPLE_INTERVAL_MS}, forever. "Nothing is spawned on
 * the sampling path" would then hold only while there was nothing to worry
 * about.
 *
 * Negative results are therefore memoised too, with a longer backoff than the
 * sample interval: a container that is absent now is unlikely to be back within
 * seconds, and the cost of noticing it late is one stale interval on a gauge,
 * against a subprocess-per-tick on an already-unhealthy agent.
 *
 * Caught by review (CodeRabbit, PR #4502).
 */
const resolveRetryAfter = new Map<string, number>();

/** How long to wait before re-attempting a resolution that failed. */
export const RESOLVE_RETRY_BACKOFF_MS = 5 * 60_000;

/**
 * Resolve the host-side cgroup directory for `agt-<codeName>`.
 *
 * Two layouts exist and which one you get depends on Docker's cgroup driver, so
 * both are probed rather than assumed:
 *
 *   systemd driver:  /sys/fs/cgroup/system.slice/docker-<id>.scope
 *   cgroupfs driver: /sys/fs/cgroup/docker/<id>
 *
 * A cached entry is revalidated with `existsSync` on every call: a container
 * that restarted has a NEW id, and the old directory is gone. Without that check
 * a restarted agent would report its predecessor's final reading forever — a
 * stuck gauge, which is worse than no gauge because it looks alive.
 */
export function resolveCgroupDirReal(
  codeName: string,
  // Seams so the CACHE logic is testable without docker or a filesystem. The
  // cache is the only stateful part of this module and it already produced one
  // real bug (deleting by value instead of by key, which silently turned the
  // cache into a per-sample `docker inspect` — the exact thing it exists to
  // prevent). Untestable state is how that ships twice.
  deps: {
    exists?: (p: string) => boolean;
    inspectId?: (codeName: string) => string | null;
    /** Injected so backoff is testable without faking timers. */
    now?: () => number;
  } = {},
): string | null {
  const exists = deps.exists ?? existsSync;
  const inspectId = deps.inspectId ?? inspectContainerId;
  const now = deps.now ?? Date.now;

  const cached = cgroupDirCache.get(codeName);
  // A VALID cached directory always wins, checked before the backoff gate — a
  // healthy agent must never be held off by a stale failure record.
  if (cached && exists(cached)) {
    resolveRetryAfter.delete(codeName);
    return cached;
  }

  const retryAt = resolveRetryAfter.get(codeName);
  if (retryAt != null && now() < retryAt) return null;
  // Delete by KEY, not by the cached value. Deleting `cached` here would be a
  // no-op (the dir is a value, never a key), leaving the stale entry in place so
  // every later call re-checked a directory that is gone and re-ran
  // `docker inspect` — turning the cache into a per-sample subprocess.
  if (cached) cgroupDirCache.delete(codeName);

  const id = inspectId(codeName);
  // Validate before interpolating into a path. `docker inspect -f` output is
  // trusted-ish, but a container id is a known shape and anything else means we
  // misread the output rather than that we found an unusual id.
  if (id == null || !/^[0-9a-f]{12,64}$/.test(id)) {
    resolveRetryAfter.set(codeName, now() + RESOLVE_RETRY_BACKOFF_MS);
    return null;
  }

  // Two layouts, depending on Docker's cgroup driver. Probed, never assumed.
  for (const dir of [
    `/sys/fs/cgroup/system.slice/docker-${id}.scope`,
    `/sys/fs/cgroup/docker/${id}`,
  ]) {
    if (exists(dir)) {
      cgroupDirCache.set(codeName, dir);
      resolveRetryAfter.delete(codeName);
      return dir;
    }
  }
  // The container exists but neither cgroup layout is where we expect. Backed
  // off too: re-running `docker inspect` every tick will not make the kernel
  // put the directory somewhere else.
  resolveRetryAfter.set(codeName, now() + RESOLVE_RETRY_BACKOFF_MS);
  return null;
}

/**
 * The ONE subprocess in this module, and it runs only on a cache miss — i.e.
 * once per container lifetime, not once per sample. See the module docstring.
 */
function inspectContainerId(codeName: string): string | null {
  // ENG-9781: hard-bounded like every other docker call the manager makes. This
  // one runs on a cache miss rather than every tick, so it is the least exposed
  // of them — but "rare" is not "safe" when the failure mode is the event loop
  // stopping for all 12 agents, and a wedged daemon is exactly when a container
  // lookup is most likely to miss its cache.
  //
  // Container not running, docker unavailable, no answer: all "no signal", not
  // an error to surface.
  const out = hardBoundedCapture('docker', ['inspect', '-f', '{{.Id}}', `agt-${codeName}`], {
    timeoutMs: 5_000,
  });
  return out === null ? null : out.trim();
}

/**
 * Count defunct (`Z`-state) processes in the cgroup.
 *
 * NOT called "zombie" anywhere in the public shape: `zombie` is already taken in
 * this codebase and means something else entirely — ENG-5391's
 * "zombie-claude-in-live-tmux" is a tmux session with no live `claude` process.
 * A field named `zombieCount` would be read as that concept by anyone who has
 * touched the reaper.
 *
 * `cgroup.procs` lists thread-group leaders, which is what we want: a defunct
 * process appears there, its threads do not.
 *
 * `/proc/<pid>/stat` is `pid (comm) state ...`, and `comm` may itself contain
 * spaces and parentheses — so the state is parsed after the LAST ')', never by
 * splitting on whitespace. Splitting naively misreads any process whose name
 * contains a space, which for node subprocesses is common.
 */
function countDefunct(cgroupDir: string, readText: (p: string) => string | null): number {
  const procs = readText(`${cgroupDir}/cgroup.procs`);
  if (procs == null) return 0;

  let defunct = 0;
  for (const line of procs.split('\n')) {
    const pid = line.trim();
    if (!pid) continue;
    const stat = readText(`/proc/${pid}/stat`);
    if (stat == null) continue; // exited between listing and reading; not an error
    const close = stat.lastIndexOf(')');
    if (close < 0) continue;
    const state = stat.slice(close + 1).trim().charAt(0);
    if (state === 'Z') defunct += 1;
  }
  return defunct;
}

/**
 * Sample one agent. Returns `null` for NO SIGNAL — never a zero-filled sample.
 *
 * The isolation gate is first and is not an optimisation: a non-isolated agent
 * has no per-agent cgroup at all, and anything this returned for one would be a
 * measurement of the host wearing the agent's name.
 */
export function samplePidPressureFor(
  codeName: string,
  io: PidPressureIo = {},
): PidPressureSample | null {
  // ─── RECORDED DECISION (ENG-8876 AC6): bare-tmux agents get NO init, and
  // ─── that is correct rather than merely tolerated.
  //
  // ENG-8825's fix was `--init` on the agent CONTAINER, so the obvious worry is
  // that non-isolated agents were left leaking. They were not, and the reason is
  // worth writing down because "we only fixed half the fleet" is the natural
  // reading of that PR and it is wrong.
  //
  // The zombie leak was a PID-NAMESPACE artifact, not a general one. Inside a
  // container without `--init`, PID 1 of that namespace is the wrapper that
  // execs `claude`. An orphaned process reparents to PID 1 *of its namespace* —
  // i.e. to claude, which never calls wait() and should not have to. So the
  // orphan stays defunct for the life of the container.
  //
  // A bare-tmux agent has no PID namespace of its own. Its orphans reparent to
  // the HOST's PID 1, which is systemd, which reaps. (Supporting evidence that
  // these are systemd hosts rather than an assumption: Docker lays their
  // container cgroups out under `/sys/fs/cgroup/system.slice/`, the systemd
  // driver's path — see resolveCgroupDirReal above.)
  //
  // Decision: (b) — accept, with cause. No init is added at the
  // host-provisioning layer, because the host already has one doing the job.
  //
  // Falsifier, so this can be checked rather than believed: if a host ever runs
  // agents under a process that has called PR_SET_CHILD_SUBREAPER and does not
  // wait() (a supervisor, a shell wrapper, a future pane manager), orphans would
  // reparent to THAT instead of systemd and this reasoning collapses. The check
  // is one command on the host: `ps -eo stat= | grep -c '^Z'`. A non-trivial
  // count on a host running only bare-tmux agents refutes this comment.
  //
  // Consequence for THIS module: a non-isolated agent has no per-agent cgroup to
  // read, so it reports null — and that null is an accurate "nothing to measure
  // here", not a coverage gap to be filled later with a host-wide number that
  // would mean something different.
  const isolated = io.isolatedFor?.(codeName) ?? isolationMode(codeName) === 'docker';
  if (!isolated) return null;

  const readText = io.readText ?? readTextReal;
  const resolveDir = io.resolveCgroupDir ?? resolveCgroupDirReal;

  const dir = resolveDir(codeName);
  if (dir == null) return null;

  // The blank check is NOT redundant with the isFinite guard below, and getting
  // this wrong is the module's own worst failure mode. `Number('')` and
  // `Number('   ')` are both **0**, which is finite and non-negative — so a
  // present-but-empty `pids.current` (a truncated read, a cgroup mid-teardown)
  // would sail through and report a container running ZERO tasks. That is the
  // false-healthy reading this whole module exists to prevent, arriving through
  // its own front door.
  //
  // Caught by review. Worth noting I had already guarded `pids.max` against
  // exactly this with its `> 0` check and did not carry the same reasoning one
  // field across.
  const currentRaw = readText(`${dir}/pids.current`)?.trim();
  if (currentRaw == null || currentRaw === '') return null;
  const current = Number(currentRaw);
  if (!Number.isFinite(current) || current < 0) return null;

  // `pids.max` is the literal string "max" when the cgroup is unlimited. That is
  // a real state, so it maps to null rather than to a sentinel number that would
  // compare wrongly against the ceiling.
  //
  // Only ONE guard, deliberately. An earlier version also compared the raw text
  // to 'max' before parsing; mutation testing showed that branch was an
  // EQUIVALENT MUTANT — removing it changed no behaviour, because Number('max')
  // is NaN and the isFinite check below already rejects it. Two guards where one
  // suffices reads as though the second catches a case the first does not, and
  // the next person has to work out that it doesn't.
  // Note the coercions this leans on, all of which land on null: Number('max')
  // and Number(undefined) are NaN (unreadable file, or the unlimited sentinel),
  // and Number('') is 0, which the `> 0` check rejects (empty file).
  const maxParsed = Number(readText(`${dir}/pids.max`)?.trim());
  const max = Number.isFinite(maxParsed) && maxParsed > 0 ? maxParsed : null;

  const ceiling = configuredCeiling();
  const warnAt = Math.floor(ceiling * warnFraction());

  return {
    current,
    max,
    ceiling,
    defunct: countDefunct(dir, readText),
    warnAt,
    overThreshold: current >= warnAt,
    // ENG-10370. Same directory, same `readText` seam, no extra resolution and
    // nothing spawned - the module's headline guarantee is unchanged because
    // this is one more `readFileSync` against a path already resolved.
    //
    // CGROUP v1 IS RULED OUT HERE BY CONSTRUCTION, not by a fallback, and the
    // mechanism is worth naming because the ticket asked for v1 to be "handled
    // or explicitly ruled out". `resolveCgroupDirReal` probes exactly two
    // layouts - `/sys/fs/cgroup/system.slice/docker-<id>.scope` and
    // `/sys/fs/cgroup/docker/<id>` - and BOTH are unified (v2) paths. A v1 host
    // splits cgroups by controller (`/sys/fs/cgroup/memory/docker/<id>`), so
    // neither probe hits, the resolver returns null, and this function has
    // already returned null above for PID pressure too.
    //
    // So on a v1 host this reports NOTHING rather than a false zero. That is
    // the silent-zero failure the AC was worried about, and it is structurally
    // impossible on this path rather than merely guarded against. If per-agent
    // cgroups ever land on a v1 host, the resolver is the single place that has
    // to learn about it, and both gauges follow.
    memory: parseMemoryEvents(readText(`${dir}/memory.events`)),
  };
}

/** Last sample per agent, read by the heartbeat accessor. */
const lastSample = new Map<string, PidPressureSample>();

/**
 * One sampling pass over the live agent set.
 *
 * Exported for tests and driven by {@link startPidPressureSampler}.
 */
export function samplePidPressureOnce(codeNames: Iterable<string>, io: PidPressureIo = {}): void {
  const seen = new Set<string>();
  for (const codeName of codeNames) {
    seen.add(codeName);
    const sample = samplePidPressureFor(codeName, io);
    if (sample) lastSample.set(codeName, sample);
    else lastSample.delete(codeName);
  }
  // Drop agents that are no longer live, so a codename reused by a new session
  // cannot inherit the dead one's reading.
  for (const codeName of [...lastSample.keys()]) {
    if (!seen.has(codeName)) {
      lastSample.delete(codeName);
      cgroupDirCache.delete(codeName);
      resolveRetryAfter.delete(codeName);
    }
  }
}

/**
 * Accessor the manager injects into `collectDiagnostics`.
 *
 * Returns the LAST SAMPLE rather than measuring inline, so the heartbeat never
 * blocks on filesystem work and can never be the thing that spawns a `docker
 * inspect`. `null` = no signal, and consumers must skip it.
 */
export function pidPressureFor(codeName: string): PidPressureSample | null {
  return lastSample.get(codeName) ?? null;
}

let timer: NodeJS.Timeout | null = null;

/**
 * Start the sampler. `liveAgents` is called each tick rather than captured, so
 * the sampler always sees the current session set without holding a reference to
 * mutable manager state.
 */
export function startPidPressureSampler(liveAgents: () => Iterable<string>): void {
  if (timer) return;
  timer = setInterval(() => {
    try {
      samplePidPressureOnce(liveAgents());
    } catch {
      // Never let a sampling error kill the manager's event loop. A missed tick
      // costs one interval of staleness on a level that moves by ~1 per 36s.
    }
  }, SAMPLE_INTERVAL_MS);
  // Telemetry must not hold the process open.
  timer.unref?.();
}

export function stopPidPressureSampler(): void {
  if (!timer) return;
  clearInterval(timer);
  timer = null;
}

/** Test seam. */
export function __resetPidPressureStateForTests(): void {
  lastSample.clear();
  cgroupDirCache.clear();
  resolveRetryAfter.clear();
  stopPidPressureSampler();
}
